import React, { useMemo, useState } from 'react';

import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Table, { TableActionBar, TableCell, TableRow } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import Row from '@amzn/meridian/row';
import {
  displayHc,
  displayThrowRate,
  displayTime,
  getBreaks,
  isBreakQuarter,
  isFeatureFlagEnabled
} from '../../../utils';
import { chainWalk } from '../../../../../utils/helpers';
import { useRoleSpeedMapping } from '../../../../../utils/roleSpeedUtils';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import chevronRightSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';
import { BUSINESS_VIEW_NAME, DEFAULT_SPEED, ALL_SPEEDS, SSD_FEATURE_FLAGS_LIST } from '../../../../../utils/constants';
export const SINGLES_ROLES = ['singlespick', 'singlespack'];

const shouldDisplayRoleRate = (department, role) => {
  return department.name !== 'injection' && role.category.toLowerCase() === 'demand';
};

const displayAvgExpectedRate = (department, breaks, role, quarter, depShiftAvgRate) => {
  if (isBreakQuarter(quarter, breaks)) {
    return 0;
  }
  let avgExpectedRate = 0;
  if (depShiftAvgRate) {
    depShiftAvgRate.forEach((sAvgRate) => {
      const rateData = sAvgRate.find(
        (r) =>
          r.process_path === department.name &&
          r.process_role === role.role.name &&
          quarter >= r.start_datetime_local &&
          quarter <= r.end_datetime_local
      );
      if (rateData) {
        avgExpectedRate = rateData.avg_expected_rate_per_hour;
      }
    });
  }
  return avgExpectedRate;
};

const populateRoleHcRate = (department, allRoleHc, roleHcRate) => {
  allRoleHc.forEach((roleHc) => {
    const prevHc = roleHcRate.find((r) => r.process_path === department.name && r.process_role === roleHc.process_role);

    if (prevHc) {
      prevHc.total_expected_rate_per_hour =
        parseFloat(roleHc ? roleHc.expected_rate_per_hour : 0) +
        parseFloat(prevHc ? prevHc.total_expected_rate_per_hour : 0);
      prevHc.end_datetime_local = roleHc.end_datetime_local;
    } else {
      const data = {
        process_path: department.name,
        process_role: roleHc.process_role,
        start_datetime_local: roleHc.start_datetime_local,
        end_datetime_local: roleHc.end_datetime_local,
        total_expected_rate_per_hour: parseFloat(roleHc ? roleHc.expected_rate_per_hour : 0)
      };
      roleHcRate.push(data);
    }
  });
};

const TimeWindowHeader = ({ quarters, index, quarter, shift, timeline, speed }) => {
  const text =
    index === quarters.length - 1
      ? `${displayTime(quarter)} - ${displayTime(shift.end)}`
      : `${displayTime(quarter)} - ${displayTime(quarters[index + 1])}`;
  return (
    <Column>
      <Text type="h200">
        {speed === DEFAULT_SPEED && isBreakQuarter(quarter, getBreaks(timeline)) ? 'Break' : ''}&nbsp;
      </Text>
      <Text type="h200">{text}</Text>
    </Column>
  );
};

const filterHeadcountBySpeed = (headcountAllocation, speed, roleSpeedMapping) => {
  return headcountAllocation.filter((headcountAllocationItem) => {
    // Get the speed from the role mapping using process_role
    const roleSpeed = roleSpeedMapping[headcountAllocationItem.process_role] || DEFAULT_SPEED;

    if (roleSpeed === speed) {
      return true;
    }

    if (speed === DEFAULT_SPEED && roleSpeed === DEFAULT_SPEED) {
      return true;
    }

    return false;
  });
};

const filterRolesBySpeed = (roles, speed) => {
  return roles.filter((role) => {
    if (role.role && role.role.speed && role.role.speed === speed) {
      return true;
    }

    if (speed === DEFAULT_SPEED && (!role.role.speed || role.role.speed === DEFAULT_SPEED)) {
      return true;
    }

    return false;
  });
};

const DepartmentTable = ({
  roles,
  headcountAllocation,
  department,
  shift,
  timeline,
  singlesEnabled,
  allQuarters,
  business,
  speed
}) => {
  const breaks = getBreaks(timeline);

  const roleSpeedMapping = useRoleSpeedMapping(roles);
  const speedFilteredHeadcount = filterHeadcountBySpeed(headcountAllocation, speed, roleSpeedMapping);
  const speedFilteredRoles = filterRolesBySpeed(roles, speed);
  const getDepartmentAvgExpectedRate = (department, breaks, allShiftRec) => {
    const quarters = department.quarters;
    let idx = 0;
    let shiftRecParts = [];
    let shiftRecRate = [];
    let roleHcRate = [];
    allQuarters.forEach((quarter, index) => {
      const allRoleHc = allShiftRec.filter(
        (r) => r.process_path === department.name && r.start_datetime_local === quarter
      );
      if (isBreakQuarter(quarter, breaks)) {
        if (idx !== 0) {
          shiftRecParts.push(idx);
          shiftRecRate.push(roleHcRate);
        }

        idx = 0;
        roleHcRate = [];
      } else {
        populateRoleHcRate(department, allRoleHc, roleHcRate);
        idx++;
      }
    });
    if (idx !== 0) {
      shiftRecParts.push(idx);
      shiftRecRate.push(roleHcRate);
    }

    shiftRecRate.forEach((recRate, index) => {
      const quarters = shiftRecParts[index];
      recRate.forEach((r) => {
        r['parts'] = quarters;
        r['avg_expected_rate_per_hour'] = parseFloat(r.total_expected_rate_per_hour) / quarters;
      });
    });

    return shiftRecRate;
  };

  const allShiftRecords = speedFilteredHeadcount.filter((rec) => {
    return rec.start_datetime_local >= shift.start && rec.start_datetime_local < shift.end;
  });

  const avgExpectedRate = useMemo(() => {
    return getDepartmentAvgExpectedRate(department, breaks, allShiftRecords);
  }, [department, breaks, allShiftRecords]);

  const roleList = speedFilteredRoles.filter((r) => {
    // Always filter by department
    const isSameDepartment = r.role.departmentName === department.name;

    // Optionally apply Singles logic
    if (SINGLES_ROLES.includes(r.role.name) && business === BUSINESS_VIEW_NAME.CORE) {
      return isSameDepartment && singlesEnabled;
    }

    // Filter by business
    if (business === r.role.businessType) {
      return isSameDepartment;
    } else if (r.role.businessType == null) {
      // default roles with no business to Core
      return business === BUSINESS_VIEW_NAME.CORE && isSameDepartment;
    }

    return false;
  });
  const [expandedRows, setExpandedRows] = useState([]);
  const updateExpandedRows = (row) => {
    const newExpandedRows = expandedRows.includes(row)
      ? expandedRows.filter((expandedRow) => expandedRow !== row)
      : [...expandedRows, row];
    setExpandedRows(newExpandedRows);
  };
  const roleRows = [];
  roleList.forEach((role) => {
    const rowIdentifier = `${speed}-${role.role.name}`;
    roleRows.push(
      <TableRow key={`${role.role.name}-headcount`}>
        <TableCell width={50}>
          <Row>
            {shouldDisplayRoleRate(department, role) ? (
              <Button type="icon" size="small" onClick={() => updateExpandedRows(rowIdentifier)}>
                <Icon
                  tokens={expandedRows.includes(rowIdentifier) ? chevronDownSmallTokens : chevronRightSmallTokens}
                />
              </Button>
            ) : (
              <div />
            )}
          </Row>
        </TableCell>
        <TableCell>
          <Text type={'h100'}>{role.role.label}</Text>
        </TableCell>
        {department.quarters.map((quarter) => {
          const roleHc = speedFilteredHeadcount.find(
            (r) =>
              r.process_path === department.name &&
              r.process_role === role.role.name &&
              r.start_datetime_local === quarter
          );
          return (
            <TableCell key={`${role.role.name}-headcount-${quarter}`}>
              {displayHc(roleHc)}
              {displayThrowRate(roleHc)}
            </TableCell>
          );
        })}
      </TableRow>
    );
    if (expandedRows.includes(rowIdentifier) && shouldDisplayRoleRate(department, role)) {
      roleRows.push(
        <TableRow key={`${role.role.name}-rate`}>
          <TableCell />
          <TableCell>Expected Rate</TableCell>
          {department.quarters.map((quarter) => {
            return (
              <TableCell key={`${role.role.name}-rate-${quarter}`}>
                {chainWalk(
                  () => displayAvgExpectedRate(department, breaks, role, quarter, avgExpectedRate).toFixed(0),
                  ''
                )}
              </TableCell>
            );
          })}
        </TableRow>
      );
    }
  });

  if (roleList.length === 0) {
    return <div></div>;
  }

  return (
    <div className="horizontal-scroll" style={{ backgroundColor: '#fff' }}>
      <Table headerColumns={1} headerRows={1} showDividers={true} showStripes={false} spacing="small">
        <TableRow highlightOnHover={true}>
          <TableCell />
          <TableCell />
          {department['quarters'].map((val, index) => (
            <TableCell key={index}>
              <TimeWindowHeader
                timeline={timeline}
                quarters={department.quarters}
                shift={shift}
                index={index}
                quarter={val}
                speed={speed}
              />
            </TableCell>
          ))}
        </TableRow>
        <TableRow backgroundColor="secondary">
          <TableCell columnSpan={2}>
            <Text>{department.label} Totals</Text>
          </TableCell>
          {department.quarters.map((quarter) => {
            const departmentHc = roleList
              .map((role) =>
                chainWalk(
                  () =>
                    speedFilteredHeadcount.find(
                      (r) =>
                        r.process_path === department.name &&
                        r.process_role === role.role.name &&
                        r.start_datetime_local === quarter
                    ).total_hc,
                  0
                )
              )
              .reduce((departmentHc, roleHc) => departmentHc + roleHc, 0);
            return <TableCell key={`${department.name}-${quarter}`}>{departmentHc}</TableCell>;
          })}
        </TableRow>
        {roleRows}
      </Table>
    </div>
  );
};

const SpeedTable = ({
  speed,
  departments,
  roles,
  headcountAllocation,
  shift,
  timeline,
  singlesEnabled,
  allQuarters,
  business,
  displayHeader
}) => {
  // Expand 5H (DEFAULT_SPEED) by default, collapse others
  const [isExpanded, setIsExpanded] = useState(speed === DEFAULT_SPEED);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Column spacing="300" width="100%">
      {displayHeader && (
        <TableActionBar
          alignmentHorizontal="justify"
          backgroundColor="primary"
          style={{ cursor: 'pointer' }}
          onClick={toggleExpanded}>
          <Row alignmentVertical="center" spacing="200">
            <Button type="icon" size="small" onClick={toggleExpanded}>
              <Icon tokens={isExpanded ? chevronDownSmallTokens : chevronRightSmallTokens} />
            </Button>
            <Text type="h300" weight="bold">
              {speed} Speed
            </Text>
          </Row>
        </TableActionBar>
      )}
      {(!displayHeader || isExpanded) && (
        <div>
          {departments.map((department) => (
            <DepartmentTable
              key={`${speed}-${department.name}`}
              roles={roles}
              timeline={timeline}
              headcountAllocation={headcountAllocation}
              department={department}
              shift={shift}
              singlesEnabled={singlesEnabled}
              allQuarters={allQuarters}
              business={business}
              speed={speed}
            />
          ))}
        </div>
      )}
    </Column>
  );
};

const ShiftHeadcountRecommendationTable = ({
  shift,
  departments,
  roles,
  headcountAllocation,
  timeline,
  singlesEnabled,
  allQuarters,
  business,
  plan
}) => {
  const isRushIndirectRulesEnabled = isFeatureFlagEnabled(
    plan.input.adminSettings,
    SSD_FEATURE_FLAGS_LIST.rushIndirectRulesEnabled
  );

  const speeds = isRushIndirectRulesEnabled ? ALL_SPEEDS : [DEFAULT_SPEED];

  return (
    <Box type={'outline'}>
      <Column spacing="400" width="100%">
        <TableActionBar alignmentHorizontal="justify">
          <Text type="h300">Recommendation</Text>
        </TableActionBar>
        {speeds.map((speed) => (
          <SpeedTable
            key={speed}
            speed={speed}
            roles={roles}
            timeline={timeline}
            headcountAllocation={headcountAllocation}
            departments={departments}
            shift={shift}
            singlesEnabled={singlesEnabled}
            allQuarters={allQuarters}
            business={business}
            displayHeader={isRushIndirectRulesEnabled}
          />
        ))}
      </Column>
    </Box>
  );
};

export default ShiftHeadcountRecommendationTable;
